import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { PageProps } from "shared/page-props";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { styles } from "./style";
import { LeadCaptureStrength } from "./lead-capture-strength"
import { LeadCaptureStrengthType } from "type/lead-capture-strength";
import { SearchPagePromptType } from "type/search-page-prompt";
import { SearchResultsPromptType } from "type/search-results-prompt";
import { SearchDetailPromptType } from "type/search-detail-prompt";
import MuiAlert from "@mui/material/Alert";
import { saveUser } from "redux/slice/authentication";
import { RootState } from "redux/store";
import { bindActionCreators, Dispatch } from "redux";
import { User } from "model/user";
import { connect } from "react-redux";
import { Switch } from "component/shared/switch";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import * as env from "shared/env";

interface OwnProps {
	user: User;
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
	return {
		authLoading: state.authentication.loading,
		authError: state.authentication.error,
	};
}

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	saveUser,
}, dispatch);


interface State {
	saving: boolean;
	leadCaptureType?: LeadCaptureStrengthType;
	searchPagePromptType?: SearchPagePromptType;
	searchPagePromptVisits: number;
	searchResultsPromptType?: SearchResultsPromptType;
	searchResultsPromptVisits: number;
	searchDetailPromptType?: SearchDetailPromptType;
	searchDetailPromptVisits: number;
	hotsheetsInResultsLimit: boolean;
	registrationFormHeader: string;
	registrationFormFooter: string;
	requirePropertyOrganizerPassword: boolean;
}

interface Props extends
	PageProps, Router.RouteComponentProps,
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	WithStyles<typeof styles> {
}

class Component extends React.Component<Props, State> {

	public constructor(props: Props) {
		super(props);
		this.state = {
			...props.user,
			saving: false,
		}
	}

	private hideSearchPagePrompt() {
		return this.props.user.permissions.eurekaSearch
			&& this.props.user.eurekaEnabled;
	}

	private isDirty(): boolean {
		const { user } = this.props;
		const effectiveSavedCaptureType = user.leadCaptureType || LeadCaptureStrengthType.OFF;
		const effectiveCurrentCaptureType = this.state.leadCaptureType || LeadCaptureStrengthType.OFF;
		return (
			effectiveCurrentCaptureType.id !== effectiveSavedCaptureType.id ||
			(this.state.searchPagePromptType?.id ?? null) !== (user.searchPagePromptType?.id ?? null) ||
			this.state.searchPagePromptVisits !== user.searchPagePromptVisits ||
			(this.state.searchResultsPromptType?.id ?? null) !== (user.searchResultsPromptType?.id ?? null) ||
			this.state.searchResultsPromptVisits !== user.searchResultsPromptVisits ||
			(this.state.searchDetailPromptType?.id ?? null) !== (user.searchDetailPromptType?.id ?? null) ||
			this.state.searchDetailPromptVisits !== user.searchDetailPromptVisits ||
			this.state.hotsheetsInResultsLimit !== user.hotsheetsInResultsLimit ||
			this.state.registrationFormHeader !== user.registrationFormHeader ||
			this.state.registrationFormFooter !== user.registrationFormFooter ||
			this.state.requirePropertyOrganizerPassword !== user.requirePropertyOrganizerPassword
		);
	}

	private cancel() {
		const { user } = this.props;
		const { saving } = this.state;
		this.setState({ ...user, saving });
	}

	public save() {
		const { saveUser } = this.props;
			const user = {
				...this.state,
			};
			if (this.hideSearchPagePrompt()) {
				user.searchPagePromptType = SearchPagePromptType.NEVER;
				user.searchPagePromptVisits = 0;
			}
			saveUser({user});
			this.setState({saving: true});
	}

	public render() {
		const { user, classes = {}, authLoading, authError } = this.props;
		const dirty = this.isDirty();
		const {
			saving,
			leadCaptureType,
			searchPagePromptType,
			searchPagePromptVisits,
			searchResultsPromptType,
			searchResultsPromptVisits,
			searchDetailPromptType,
			searchDetailPromptVisits,
			hotsheetsInResultsLimit,
			registrationFormHeader,
			registrationFormFooter,
			requirePropertyOrganizerPassword,
		} = this.state;

		const snackbar = (
			<Mui.Snackbar
				open={saving && !authLoading && !authError}
				message="Settings saved"
				autoHideDuration={6000}
				onClose={() => this.setState({ saving: false })}
				action={
					<Mui.IconButton
						size="small"
						aria-label="close"
						color="inherit"
						onClick={() => this.setState({ saving: false })}>
						<Icons.X fontSize="small" />
					</Mui.IconButton>
				}>
					<MuiAlert severity="info">
						<Mui.Typography>Settings saved</Mui.Typography>
					</MuiAlert>
			</Mui.Snackbar>
		);

		const effectiveLeadCaptureType = leadCaptureType || LeadCaptureStrengthType.OFF;
		const promptingEnabled = effectiveLeadCaptureType !== LeadCaptureStrengthType.OFF;
		const searchResultsEnabled = (searchResultsPromptType || SearchResultsPromptType.NEVER) !== SearchResultsPromptType.NEVER;
		const propertyDetailsEnabled = (searchDetailPromptType || SearchDetailPromptType.NEVER) !== SearchDetailPromptType.NEVER;

		return (
			<DashboardLayout
				permitted={user && user.permissions.listingDetail}
				title="Lead Registration"
				header={
					<Mui.Typography variant="h1">Lead Registration</Mui.Typography>
				}
			>
				<div className={classes.pageLayout}>
				<div className={classes.cardWrapper}>
				<Mui.Typography variant="body2" className={classes.pageSubtitle}>
					Control your website's lead registration experience. Set when and how often visitors are prompted to register, choose your capture level, and customize the messaging on your registration form.
				</Mui.Typography>
				{/* Registration Prompts */}
				<Mui.Box mb={2}>
					<FullPageCard>
					<Mui.Grid container direction="column" spacing={2}>
						<Mui.Grid item>
							<Mui.Typography className={classes.sectionHeading}>Registration Prompts</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item>
							<Switch
								checked={promptingEnabled}
								onChange={value => this.setState({
									leadCaptureType: value ? LeadCaptureStrengthType.ULTRA_LIGHT_MULTI : LeadCaptureStrengthType.OFF
								})}
								label="Automatically prompt website visitors to register"
								description="When enabled, website visitors will be automatically prompted to register based on your configured capture level and trigger points."
							/>
						</Mui.Grid>

						{promptingEnabled && (<>
							{/* Lead Capture Level */}
							<Mui.Grid item>
								<div style={{ paddingLeft: 59 }}>
								<Mui.Typography className={classes.subHeading} style={{ marginBottom: 15 }}>Lead Capture Level</Mui.Typography>
								<Mui.RadioGroup
									value={effectiveLeadCaptureType.id}
									onChange={e => this.setState({ leadCaptureType: LeadCaptureStrengthType.getById(parseInt(e.target.value)) })}
								>
									<Mui.Grid container alignItems="center">
										<Mui.FormControlLabel value={LeadCaptureStrengthType.HEAVY.id} control={<Mui.Radio color="secondary" />} label="Strict" style={{ marginRight: 0 }} />
										<Mui.Tooltip title="The user must register and then wait to receive an email from the system. The email contains a link back to the website and brings the user back to the place where they were prior to registration. This method ensures that the user has provided a valid email address.">
											<span className={classes.infoIcon}><Icons.Info size={16} /></span>
										</Mui.Tooltip>
									</Mui.Grid>
									<Mui.Grid container alignItems="center">
										<Mui.FormControlLabel value={LeadCaptureStrengthType.LIGHT.id} control={<Mui.Radio color="secondary" />} label="Moderate" style={{ marginRight: 0 }} />
										<Mui.Tooltip title="The user must register when prompted. After providing information, user is directed back to the page they were trying to access when registration was triggered.">
											<span className={classes.infoIcon}><Icons.Info size={16} /></span>
										</Mui.Tooltip>
									</Mui.Grid>
									<Mui.Grid container alignItems="center">
										<Mui.FormControlLabel value={LeadCaptureStrengthType.ULTRA_LIGHT_MULTI.id} control={<Mui.Radio color="secondary" />} label="Flexible" style={{ marginRight: 0 }} />
										<Mui.Tooltip title="The user is prompted for registration, but has the option to ignore the request and continue on with property viewing. If the user ignores the prompt, the system will begin tracking user searching and viewing again. Once the user reaches the limit again, the system will prompt for registration again.">
											<span className={classes.infoIcon}><Icons.Info size={16} /></span>
										</Mui.Tooltip>
									</Mui.Grid>
									<Mui.Grid container alignItems="center">
										<Mui.FormControlLabel value={LeadCaptureStrengthType.ULTRA_LIGHT_ONCE.id} control={<Mui.Radio color="secondary" />} label="Minimal" style={{ marginRight: 0 }} />
										<Mui.Tooltip title="The user is prompted for registration, but has the option to ignore the request and continue on with searching and property viewing. If the user ignores the first prompt, they are not prompted again.">
											<span className={classes.infoIcon}><Icons.Info size={16} /></span>
										</Mui.Tooltip>
									</Mui.Grid>
								</Mui.RadioGroup>
								</div>
							</Mui.Grid>

							{/* Trigger Points */}
							<Mui.Grid item>
								<div style={{ paddingLeft: 59 }}>
								<Mui.Typography className={classes.subHeading}>Trigger Points</Mui.Typography>
								<Mui.Typography className={classes.contentDescription}>
									Prompt leads to register when they are viewing:
								</Mui.Typography>
								<Mui.Grid container direction="column">

									{/* Search Results */}
									<Mui.FormControlLabel
										control={
											<Mui.Checkbox
												checked={searchResultsEnabled}
												onChange={e => this.setState({
													searchResultsPromptType: e.target.checked
														? SearchResultsPromptType.IMMEDIATELY
														: SearchResultsPromptType.NEVER
												})}
												color="secondary"
											/>
										}
										label="Search Results"
									/>
									{searchResultsEnabled && (
										<Mui.Grid container direction="column" className={classes.indented}>
											<Mui.Typography className={classes.selectFrequency}>Select frequency below:</Mui.Typography>
											<Mui.RadioGroup
												value={(searchResultsPromptType || SearchResultsPromptType.IMMEDIATELY).id}
												onChange={e => this.setState({ searchResultsPromptType: SearchResultsPromptType.getById(parseInt(e.target.value)) })}
											>
												<Mui.FormControlLabel value={SearchResultsPromptType.IMMEDIATELY.id} control={<Mui.Radio color="secondary" />} label="Immediately" />
												<Mui.Grid container alignItems="center">
													<Mui.FormControlLabel value={SearchResultsPromptType.AFTER.id} control={<Mui.Radio color="secondary" />} label="After" />
													<Mui.TextField
														value={searchResultsPromptVisits}
														onChange={e => this.setState({ searchResultsPromptVisits: parseInt(e.target.value) || 0 })}
														className={classes.pagesField}
														InputProps={{ type: "number", inputProps: { min: 0 } }}
														variant="outlined"
														size="small"
													/>
													<Mui.Typography className={classes.limitFieldLabel}>pages of results</Mui.Typography>
												</Mui.Grid>
											</Mui.RadioGroup>
											<Switch
												checked={hotsheetsInResultsLimit}
												onChange={value => this.setState({ hotsheetsInResultsLimit: value })}
												label="Include saved search page views in results page limit"
												labelBold={false}
											/>
										</Mui.Grid>
									)}

									{/* Property Details */}
									<Mui.FormControlLabel
										control={
											<Mui.Checkbox
												checked={propertyDetailsEnabled}
												onChange={e => this.setState({
													searchDetailPromptType: e.target.checked
														? SearchDetailPromptType.IMMEDIATELY
														: SearchDetailPromptType.NEVER
												})}
												color="secondary"
											/>
										}
										label="Property Details"
									/>
									{propertyDetailsEnabled && (
										<Mui.Grid container direction="column" className={classes.indented}>
											<Mui.Typography className={classes.selectFrequency}>Select frequency below:</Mui.Typography>
											<Mui.RadioGroup
												value={(searchDetailPromptType || SearchDetailPromptType.IMMEDIATELY).id}
												onChange={e => this.setState({ searchDetailPromptType: SearchDetailPromptType.getById(parseInt(e.target.value)) })}
											>
												<Mui.FormControlLabel value={SearchDetailPromptType.IMMEDIATELY.id} control={<Mui.Radio color="secondary" />} label="Immediately" />
												<Mui.Grid container alignItems="center">
													<Mui.FormControlLabel value={SearchDetailPromptType.AFTER.id} control={<Mui.Radio color="secondary" />} label="After" />
													<Mui.TextField
														value={searchDetailPromptVisits}
														onChange={e => this.setState({ searchDetailPromptVisits: parseInt(e.target.value) || 0 })}
														className={classes.pagesField}
														InputProps={{ type: "number", inputProps: { min: 0 } }}
														variant="outlined"
														size="small"
													/>
													<Mui.Typography className={classes.limitFieldLabel}>pages of results</Mui.Typography>
												</Mui.Grid>
											</Mui.RadioGroup>
										</Mui.Grid>
									)}
								</Mui.Grid>
								</div>
							</Mui.Grid>
						</>)}
					</Mui.Grid>
					</FullPageCard>
				</Mui.Box>

				{/* Registration Form Text */}
				<Mui.Box mb={2}>
					<FullPageCard>
					<Mui.Grid container direction="column" spacing={2}>
						<Mui.Grid item>
							<Mui.Typography className={classes.sectionHeading} style={{ marginBottom: 20 }}>Registration Form Text</Mui.Typography>
							<Mui.Typography className={classes.helperText}>
								Customize the header and footer text displayed on the registration form shown to visitors on your website.
							</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Typography className={classes.textFieldLabel}>Header Text</Mui.Typography>
							<Mui.TextField
								value={registrationFormHeader}
								onChange={e => this.setState({ registrationFormHeader: e.target.value })}
								variant="outlined"
								fullWidth
								multiline
								minRows={4}
							/>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Typography className={classes.textFieldLabel}>Footer Text</Mui.Typography>
							<Mui.TextField
								value={registrationFormFooter}
								onChange={e => this.setState({ registrationFormFooter: e.target.value })}
								variant="outlined"
								fullWidth
								multiline
								minRows={4}
							/>
						</Mui.Grid>
					</Mui.Grid>
					</FullPageCard>
				</Mui.Box>


				<Mui.Box mb={2}>
					<FullPageCard>
					<Mui.Grid container direction="column" spacing={2}>
						<Mui.Grid item>
							<Mui.Typography className={classes.sectionHeading}>Property Organizer</Mui.Typography>
						</Mui.Grid>
						<Mui.Grid item>
							<Switch
								checked={requirePropertyOrganizerPassword}
								onChange={value => this.setState({ requirePropertyOrganizerPassword: value })}
								label="Require leads to set password"
								description="When enabled, website visitors must create a password during registration. When disabled, website visitors can log in with their email address alone."
							/>
						</Mui.Grid>
					</Mui.Grid>
					</FullPageCard>
				</Mui.Box>
				</div>
				<SettingsStickyFooter
					dirty={dirty}
					saving={saving}
					authError={authError}
					onSave={() => this.save()}
					onCancel={() => this.cancel()}
				/>
				</div>
				{snackbar}
			</DashboardLayout>
		);
	}
}

export const SettingsUserRegistrationPage = withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles);
